import {ValidatableEmail} from 'Core/models/Email';
import {ValidatablePhone} from 'Core/models/PhoneNumber';
import {ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import * as I from 'immutable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import * as BUI from 'Users/models/User/BasicInformation';
import {
  areAllUserTypesAllowedForExternal,
  areAllUserTypesValid,
  isAgentUserType,
  matchUserTypeByLineOfBusiness,
  UserType,
  ValidUserType, 
  isQuotingUserType,
} from 'Users/models/User/UserType';

export class UnvalidatedUser {
  public readonly key = 'UnvalidatedUser';

  constructor(public readonly basicInformation: BUI.BasicInformation, public readonly userTypes: I.List<UserType>) {}
}

export class InvalidUser {
  public readonly key = 'InvalidUser';

  constructor(public readonly basicInformation: BUI.BasicInformation, public readonly userTypes: I.List<UserType>) {}
}

export class ValidUser {
  public static create(basicInformation: BUI.BasicInformation, userTypes: I.List<UserType>) {
    const userTypesArray = userTypes.toArray();
    const haveAtLeastOneUserType = userTypesArray?.length > 0;

    return BUI.isValidBasicInformation(basicInformation) &&
      haveAtLeastOneUserType &&
      areAllUserTypesValid(userTypesArray) &&
      (basicInformation.accountType === 'Internal' || areAllUserTypesAllowedForExternal(userTypesArray))
      ? new ValidUser(basicInformation, I.List(userTypesArray))
      : new InvalidUser(basicInformation, userTypes);
  }

  public readonly key = 'ValidUser';
  private constructor(
    public readonly basicInformation: BUI.ValidInternalBasicInformation | BUI.ValidExternalBasicInformation,
    public readonly userTypes: I.List<ValidUserType>
  ) {}
}

export class LoadingUser {
  public readonly key = 'LoadingUser';

  constructor(public readonly basicInformation: BUI.BasicInformation, public readonly userTypes: I.List<UserType>) {}
}

export class UserWithErrors {
  public readonly key = 'UserWithErrors';

  constructor(public readonly basicInformation: BUI.BasicInformation, public readonly userTypes: I.List<UserType>) {}
}

export const changeAccountType = (accountType: BUI.AccountType) => (user: User) =>
  changeBasicInformation(user)(BUI.changeAccountType(accountType)(user.basicInformation));
export const changeFirstName = (firstName: string) => (user: User) =>
  changeBasicInformation(user)(BUI.changeFirstName(firstName)(user.basicInformation));
export const changeLastName = (lastName: string) => (user: User) =>
  changeBasicInformation(user)(BUI.changeLastName(lastName)(user.basicInformation));
export const changeUserName = (userName: ValidatableNonEmptyString) => (user: User) =>
  changeBasicInformation(user)(BUI.changeUserName(userName)(user.basicInformation));
export const changeEmail = (email: ValidatableEmail) => (user: User) =>
  changeBasicInformation(user)(BUI.changeEmail(email)(user.basicInformation));
export const changePhoneNumber = (phoneNumber: ValidatablePhone) => (user: User) =>
  changeBasicInformation(user)(BUI.changePhoneNumber(phoneNumber)(user.basicInformation));
export const changeIsUserSupport = (isUserSupport: boolean) => (user: User) =>
  changeBasicInformation(user)(BUI.changeIsUserSupport(isUserSupport)(user.basicInformation));
  export const changeIsUserDisable = (isDisabled: boolean) => (user: User) =>
  changeBasicInformation(user)(BUI.changeIsUserDisable(isDisabled)(user.basicInformation));
export const changeBasicInformation = ({userTypes}: User) => (basicInformation: BUI.BasicInformation) =>
  ValidUser.create(basicInformation, userTypes);

export const addUserType = (userType: UserType) => ({basicInformation, userTypes}: User) => {
  const existingUserTypes: I.List<UserType> = userTypes;

  return ValidUser.create(basicInformation, existingUserTypes.push(userType));
};

export const removeUserType = (userType: UserType) => ({basicInformation, userTypes}: User) =>
  ValidUser.create(
    basicInformation,
    userTypes.filter(ut => ut.value.key !== userType.value.key)
  );

export const resetUserType = (basicInformation: BUI.BasicInformation) => ValidUser.create(basicInformation, I.List<UserType>());

export const changeUserType = (userType: UserType) => ({basicInformation, userTypes}: User) => {
  const existingUserTypes: I.List<UserType> = userTypes;
  const existingUserTypeValues = existingUserTypes.map(ut => ut.value);
  const updatedUserTypes = matchUserTypeByLineOfBusiness(userType.value, {
    Agent: userType => existingUserTypeValues.set(existingUserTypeValues.findIndex(isAgentUserType), userType),
    Quoting: userType => existingUserTypeValues.set(existingUserTypeValues.findIndex(isQuotingUserType), userType), 
  }).map(ValidUserType.create);

  return ValidUser.create(basicInformation, updatedUserTypes);
};
export const showError = (user: ValidUser) => new UserWithErrors(user.basicInformation, user.userTypes);

export const EmptyUser = new UnvalidatedUser(BUI.EmptyBasicInformation, I.List<UserType>());

export const isValid = (user: User): user is ValidUser => user.key === 'ValidUser';
export const isLoading = (user: User): user is LoadingUser => user.key === 'LoadingUser';
export const isUserWithErrors = (user: User): user is UserWithErrors => user.key === 'LoadingUser';

export type User = ValidUser | InvalidUser | UnvalidatedUser | LoadingUser | UserWithErrors;

export type UserOptions<TReturn> = ToMatchOptions<User, 'key', TReturn>;

export const matchUser = <TReturn>(user: User, options: UserOptions<TReturn>): TReturn => {
  switch (user.key) {
    case 'InvalidUser':
      return options.InvalidUser(user);
    case 'UnvalidatedUser':
      return options.UnvalidatedUser(user);
    case 'ValidUser':
      return options.ValidUser(user);
    case 'LoadingUser':
      return options.LoadingUser(user);
    case 'UserWithErrors':
      return options.UserWithErrors(user);
  }
};
